<template>
  <div
    ref="target"
    v-show="selfVisible"
    class="ui-dialog--wrap tw-overlay"
    :class="classNames"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="title ? 'dialog-title' : undefined"
    @click.self="handleWrapperClick"
  >
    <transition name="scale">
      <div
        v-show="selfVisible"
        ref="content"
        class="ui-dialog"
        :class="{
          'not-web tw-w-[77.78%] tw-max-w-[450px] tw-pt-[30px] tw-rounded-[12px] tw-bg-[#f8f8f8]': !isWeb,
          'is-web tw-w-[450px] tw-pt-[40px] tw-rounded-[8px] tw-bg-white': isWeb,
          'no-title': !title,
          'no-message': !message && !$slots.default,
          'ui-dialog--html': !!dangerouslyUseHTMLString,
          'ui-dialog--vnode': !!$slots.default
        }"
        :style="{
          marginTop: `${scrollMarginTop}px`,
          marginBottom: `${scrollMarginTop ? 10 : 0}px`,
          maxWidth: maxWidth
        }"
        tabindex="-1"
      >
        <slot name="close-btn">
          <button
            v-if="showClose"
            class="ui-dialog-btn-close"
            @click="handleClose"
          >
            <svg
              viewBox="0 0 1024 1024"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M551.822222 512l318.577778-318.577778c11.377778-11.377778 11.377778-28.444444 0-39.822222s-28.444444-11.377778-39.822222 0L512 472.177778 193.422222 147.911111c-11.377778-11.377778-28.444444-11.377778-39.822222 0s-11.377778 28.444444 0 39.822222L472.177778 512l-318.577778 318.577778c-11.377778 11.377778-11.377778 28.444444 0 39.822222 5.688889 5.688889 11.377778 5.688889 22.755556 5.688889s17.066667 0 22.755555-5.688889L512 551.822222l318.577778 318.577778c5.688889 5.688889 11.377778 5.688889 22.755555 5.688889s17.066667 0 22.755556-5.688889c11.377778-11.377778 11.377778-28.444444 0-39.822222L551.822222 512z"
              ></path>
            </svg>
          </button>
        </slot>

        <slot name="ui-dialog-header">
          <h3 class="ui-dialog-title" v-if="title">
            {{ title }}
          </h3>
        </slot>
        <div class="ui-dialog--main" ref="main">
          <slot v-if="message || $slots.default">
            <div
              v-if="!dangerouslyUseHTMLString"
              class="ui-dialog--body"
            >
              <p class="ui-dialog-content">
                {{ message }}
              </p>
            </div>
            <div
              v-else
              class="ui-dialog--body"
              v-html="message"
            />
          </slot>
        </div>

        <slot name="ui-dialog-footer">
          <div
            v-if="cancelButtonText || confirmButtonText"
            class="ui-dialog--footer"
            :class="[
              direction
            ]"
          >
            <CancelButton
              v-if="cancelButtonText"
              @click="handleCancel"
            >{{ cancelButtonText }}</CancelButton>

            <ConfirmButton
              v-if="confirmButtonText"
              class="tw-font-bold"
              :loading="confirmLoading"
              @click="handleConfirm"
            >{{ confirmButtonText }}</ConfirmButton>
          </div>
        </slot>

        <slot name="after-footer" />
      </div>
    </transition>

    <slot name="content-in-mask" />
  </div>
</template>

<script>
import { nextTick, ref, toRefs, watch } from '@vue/composition-api'
import { useElementSize, useEventListener } from '@vueuse/core'
import { useWindowResize } from '@ms/utils/listener'
import ConfirmButton from './ConfirmButton.vue'
import CancelButton from './CancelButton.vue'

const checkIsWeb = () => /web/.test(location.pathname) || document.documentElement.clientWidth >= 768

export default {
  name: 'UIDialog',
  components: {
    ConfirmButton,
    CancelButton
  },
  model: {
    prop: 'visible',
    event: 'hide'
  },
  props: {
    isWeb: {
      type: Boolean,
      default: checkIsWeb()
    },
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    confirmButtonText: {
      type: String,
      default: ''
    },
    confirmLoading: {
      type: Boolean,
      default: false
    },
    cancelButtonText: {
      type: String,
      default: ''
    },
    // close btn selfVisible
    showClose: {
      type: Boolean,
      default: true
    },
    classNames: {
      type: Array,
      default: () => []
    },
    dangerouslyUseHTMLString: {
      type: Boolean,
      default: false
    },
    // 是否可通过点击遮罩关闭
    closeOnClickModal: {
      type: Boolean,
      default: false
    },
    // footer 按钮方向，默认水平, 可选 vertical
    direction: {
      type: String,
      default: 'horizontal'
    },
    maxWidth: {
      type: String,
      default: ''
    },
    onBeforeClose: {
      type: Function,
      default: null
    },
    onClose: {
      type: Function,
      default: null
    },
    onCancel: {
      type: Function,
      default: null
    },
    onConfirm: {
      type: Function,
      default: null
    }
  },
  setup(props, { emit }) {
    const { onBeforeClose, onClose, onCancel, onConfirm, closeOnClickModal } = props
    const { visible, confirmLoading } = toRefs(props)
    const target = ref(null)
    const content = ref(null)
    const scrollMarginTop = ref(0)
    const selfVisible = ref(props.visible)

    const open = () => {
      if (selfVisible.value) return
      selfVisible.value = true
      emit('open')
    }

    const handleClose = () => {
      if (!selfVisible.value || confirmLoading.value) return
      onBeforeClose && onBeforeClose()
      selfVisible.value = false
      if (onClose) {
        onClose()
      } else {
        emit('close')
      }
      emit('hide')
    }

    const handleCancel = () => {
      if (!selfVisible.value || confirmLoading.value) return
      onBeforeClose && onBeforeClose()
      selfVisible.value = false
      if (onCancel) {
        onCancel()
      } else {
        emit('cancel')
      }
      emit('hide')
    }

    const handleConfirm = () => {
      if (confirmLoading.value) return
      if (!visible.value) {
        selfVisible.value = false
      }
      if (onConfirm) {
        onConfirm()
      } else {
        emit('confirm')
      }
      emit('hide')
    }

    const handleWrapperClick = () => {
      if (!closeOnClickModal) return
      handleClose()
    }

    const contentRefSize = useElementSize(content)

    const updateScrollStyle = () => {
      nextTick(() => {
        if (!content.value) return
        const { offsetHeight } = content.value
        const parentHeight = target.value.offsetHeight
        const maxHeight = parentHeight * 0.94

        const getScrollMarginTop = () => {
          return offsetHeight !== parentHeight && offsetHeight > maxHeight ? Math.ceil(offsetHeight - maxHeight) : 0
        }

        scrollMarginTop.value = getScrollMarginTop()
      })
    }

    watch(contentRefSize.height, updateScrollStyle)

    useWindowResize(updateScrollStyle)
    useEventListener(document, 'keydown', (e) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    })

    return { target, scrollMarginTop, updateScrollStyle, content, selfVisible, open, handleClose, handleCancel, handleConfirm, handleWrapperClick }
  },
  watch: {
    visible(value) {
      if (value) {
        this.open()
        this.updateScrollStyle()
      } else {
        this.handleClose()
        this.scrollMarginTop = 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.no-title.not-web {
  .ui-dialog--body {
    font-size: 16px;
    line-height: 20px;
  }
}

.no-title.no-message {
  padding: 0;
}

.ui-dialog {
  position: relative;
  box-sizing: border-box;
  margin: 0 auto;
  letter-spacing: 0;
  transform-origin: center;
  transition:
  opacity 0.3s,
  transform 0.3s;

  &--wrap {
    z-index: 9999;
    display: flex;
    overflow: auto;
    align-items: center;
    justify-content: center;
  }

  &.ui-dialog--vnode {
    padding-top: 0;
  }

  &-btn-close {
    position: absolute;
    z-index: 9;
    top: 0;
    right: 0;
    width: 30px;
    height: 30px;
    color: #999;
    text-align: center;

    svg {
      height: 14px;
      fill: currentcolor;
    }
  }

  &-title {
    margin: 0 auto 17px;
    color: #333;
    font-size: 20px;
    line-height: 24px;
    text-align: center;
  }

  &--body {
    padding-bottom: 30px;
    color: #333;
    font-size: 14px;
    line-height: 18px;
  }

  &-content {
    margin: 0 30px;
    word-break: break-word;
  }

  &--footer {
    padding: 0 0 20px;
    margin: 0 20px;

    &.horizontal {
      display: flex;
      justify-content: center;
      text-align: center;

      .btn--cancel {
        margin-right: 12px;
      }
    }

    &.vertical {
      display: block;
      height: auto;

      .btn {
        margin-bottom: 10px;
      }
    }
  }

  ::v-deep img {
    max-width: 100%;
  }
}

.ui-dialog.is-web {
  .ui-dialog-btn-close {
    top: 10px;
    right: 10px;
    cursor: pointer;
    font-size: 14px;
    text-align: center;
  }

  .ui-dialog-title {
    padding-top: 10px;
    margin: 0 auto 16px;
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }

  .ui-dialog--body {
    padding-bottom: 40px;
    font-size: 18px;
    line-height: 24px;
  }

  .ui-dialog-content,
  .ui-dialog--footer {
    margin: 0 50px;
  }

  .ui-dialog--footer {
    justify-content: space-between;
    padding-bottom: 40px;

    &.vertical {
      display: block;
      height: auto;

      .btn {
        width: 100%;
        margin-right: 0;
      }
    }

    .btn--cancel {
      margin-right: 30px;
    }

    .btn {
      width: 160px;
      height: 40px;
      font-size: 16px;
    }
  }
}


.scale-leave-active,
.scale-enter {
  opacity: 0;
  transform: scale3d(0, 0, 0);
}
</style>
